'use client';
import { SlideFrame } from '../SlideFrame';
import { useResource } from '@/lib/use-resource';
import { compositeScore, prodQuality } from '@/lib/composite-score';

// 2×2 LIVE tiles mirroring the actual /research-desk tab strip
// (Steve directive 2026-05-22: each tile must reflect real-time data,
// updating on every refresh — "everything in one platform" means the deck
// renders what the application renders, not stylized copies).
//
//   1. Ideas       — /api/active-idea         (today's high-conviction pick)
//   2. Movers      — /api/research-movers     (top composite movers up/down)
//   3. Leaderboard — /api/leaderboard         (biggest 36d composite gainers)
//   4. History Lab — /api/history/universe-regime (latest regime metrics)
//
// Ideas is persisted (changes infrequently); the other 3 hit fresh on each
// refresh via 60s TTL.

interface Idea {
  symbol?: string; name?: string; sector?: string;
  composite?: number; qualityScore?: number; qualityScoreBlended?: number | null; valuationScore?: number;
  action?: { type?: string; swapSource?: string; targetWeight?: number };
}
interface ActiveIdeaResp { payload?: { asOf?: string; fullConsensus?: Idea[]; threeOfFour?: Idea[] } }

interface MoverRow { symbol: string; name?: string; sector?: string; composite?: number; d20?: number }
interface CongRow  { symbol: string; name?: string; sector?: string; buy90d?: number; sell90d?: number; net90d?: number }
interface MoversResp {
  asOf?: string;
  compositeMoversUp?: MoverRow[];
  compositeMoversDown?: MoverRow[];
  congressionalTopBuys?: CongRow[];
  congressionalTopSells?: CongRow[];
}

interface GainerRow { symbol: string; name: string; sector: string; cDelta: number; composite: number }
interface LeaderboardResp {
  asOf?: string;
  lookbackDays?: number;
  lookbackAnchor?: string;
  universeSize?: number;
  movers?: { cGainers?: GainerRow[]; cLosers?: GainerRow[] };
}

interface RegimeComposite { value: number; tier: string }
interface RegimeResp {
  asOf?: string;
  ticker?: string;
  startDate?: string;
  endDate?: string;
  composites?: {
    valuationExpensiveness?: RegimeComposite;
    qualityStrength?: RegimeComposite;
  };
}

const fmtSig = (v: number | null | undefined) => (v == null || !Number.isFinite(v))
  ? '–' : `${v >= 0 ? '+' : '−'}${Math.abs(v).toFixed(2)}σ`;

export function Slide09ResearchAnalyst(_props: { universe: number }) {
  return (
    <SlideFrame pageNum={12} title="THE VISUALIZER  —  RESEARCH ANALYST"
      banner="An AI analyst that never sleeps. Four views, refreshed every morning.">
      <div className="h-full grid grid-cols-2 grid-rows-2 gap-3 px-8 py-2">
        <IdeasTile />
        <MoversTile />
        <LeaderboardTile />
        <HistoryLabTile />
      </div>
    </SlideFrame>
  );
}

function TileFrame({ num, title, asOf, children }: { num: string; title: string; asOf?: string | null; children: React.ReactNode }) {
  return (
    <div className="bg-[#1e293b] rounded-2xl border border-[#c9a84c]/30 px-4 py-2.5 flex flex-col overflow-hidden">
      <div className="flex items-baseline justify-between mb-1.5">
        <div className="flex items-baseline gap-2">
          <span className="text-[#c9a84c] text-[18px] font-bold tabular-nums leading-none">{num}.</span>
          <span className="text-[#c9a84c] text-[18px] font-bold leading-none">{title}</span>
        </div>
        {asOf && <div className="text-[9px] text-[#64748b] font-mono">{asOf}</div>}
      </div>
      <div className="flex-1 min-h-0 overflow-hidden">{children}</div>
    </div>
  );
}

// ---------- 1. IDEAS ----------
function IdeasTile() {
  const { data } = useResource<ActiveIdeaResp>('/api/active-idea', 60_000);
  const p = data?.payload;
  const idea = p?.fullConsensus?.[0] ?? p?.threeOfFour?.[0] ?? null;
  // active-idea payload often has composite: null even when Q+V are
  // populated (the research-notes writer doesn't always carry it through).
  // Fall back to computing it locally with the production composite formula.
  const composite = idea?.composite ?? (
    idea?.qualityScore != null && idea?.valuationScore != null
      ? compositeScore(prodQuality(idea), idea.valuationScore)
      : null
  );
  const tier = composite == null ? '' : composite >= 3 ? 'STRONG BUY' : composite >= 1.5 ? 'BUY' : composite >= 0 ? 'HOLD' : 'SELL';
  const tierClass = tier === 'STRONG BUY' || tier === 'BUY' ? 'bg-emerald-500/15 border-emerald-400/50 text-emerald-300'
                  : tier === 'HOLD' ? 'bg-amber-500/15 border-amber-400/50 text-amber-300'
                  : tier === 'SELL' ? 'bg-red-500/15 border-red-400/50 text-red-300'
                  : 'bg-[#1e293b] border-[#334155] text-[#cbd5e1]';
  const action = idea?.action;
  const actionLine = action?.type === 'SWAP' && action.swapSource
    ? `SWAP — sell ${action.swapSource}, buy ${idea?.symbol}`
    : action?.type === 'ADD' ? `ADD — open ${idea?.symbol}` : (action?.type ?? '');
  return (
    <TileFrame num="1" title="Ideas" asOf={p?.asOf ?? null}>
      {!idea ? (
        <div className="text-[11px] text-[#64748b] italic">No high-conviction idea today.</div>
      ) : (
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            {tier && <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md border text-[11px] font-bold tracking-wide ${tierClass}`}>{tier}</span>}
            <span className="text-white text-[16px] font-bold tabular-nums">{idea.symbol}</span>
            <span className="text-[11px] text-[#94a3b8] truncate">{idea.name}</span>
          </div>
          <div className="grid grid-cols-3 gap-2 text-[10px]">
            <ScoreMini label="Composite" value={composite?.toFixed(2) ?? '–'} tone="gold" />
            <ScoreMini label="Quality"   value={fmtSig(prodQuality(idea))} tone="emerald" />
            <ScoreMini label="Valuation" value={fmtSig(idea.valuationScore)} tone={(idea.valuationScore ?? 0) >= 0 ? 'emerald' : 'amber'} />
          </div>
          {actionLine && (
            <div className="text-[11px] bg-[#c9a84c]/10 border border-[#c9a84c]/30 rounded-md px-2.5 py-1.5">
              <div className="text-[#c9a84c] text-[8px] uppercase tracking-widest font-bold">Recommendation</div>
              <div className="text-white font-semibold leading-tight">{actionLine}</div>
            </div>
          )}
          <div className="text-[10px] text-[#64748b] italic">{idea.sector}</div>
        </div>
      )}
    </TileFrame>
  );
}

function ScoreMini({ label, value, tone }: { label: string; value: string; tone: 'gold'|'emerald'|'amber' }) {
  const toneText = tone === 'gold' ? 'text-[#c9a84c]' : tone === 'emerald' ? 'text-emerald-300' : 'text-amber-300';
  return (
    <div className="bg-[#0a0e14] rounded px-2 py-1 text-center">
      <div className="text-[8px] uppercase tracking-widest text-[#64748b] font-bold">{label}</div>
      <div className={`text-[12px] font-bold tabular-nums ${toneText}`}>{value}</div>
    </div>
  );
}

// ---------- 2. MOVERS ----------
function MoversTile() {
  const { data } = useResource<MoversResp>('/api/research-movers', 60_000);
  const up = data?.compositeMoversUp ?? [];
  const cong = data?.congressionalTopBuys ?? [];
  return (
    <TileFrame num="2" title="Movers" asOf={data?.asOf ?? null}>
      <div className="grid grid-cols-2 gap-3 h-full">
        <div className="min-h-0">
          <div className="text-[9px] uppercase tracking-wider text-emerald-300/70 mb-1 font-bold">Composite up · 20d</div>
          <ul className="space-y-0.5">
            {up.slice(0, 5).map(r => (
              <li key={r.symbol} className="grid grid-cols-[auto_1fr_auto] gap-2 items-baseline text-[11px]">
                <span className="text-white font-mono font-bold w-12">{r.symbol}</span>
                <span className="text-[#94a3b8] truncate text-[10px]">{r.name}</span>
                <span className="text-emerald-300 font-mono tabular-nums">+{(r.d20 ?? 0).toFixed(2)}σ</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="min-h-0">
          <div className="text-[9px] uppercase tracking-wider text-emerald-300/70 mb-1 font-bold">Congressional · top buys 90d</div>
          <ul className="space-y-0.5">
            {cong.slice(0, 5).map(r => (
              <li key={r.symbol} className="grid grid-cols-[auto_1fr_auto] gap-2 items-baseline text-[11px]">
                <span className="text-white font-mono font-bold w-12">{r.symbol}</span>
                <span className="text-[#94a3b8] truncate text-[10px]">{r.name}</span>
                <span className="text-emerald-300 font-mono tabular-nums">{r.buy90d ?? 0}B/{r.sell90d ?? 0}S</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </TileFrame>
  );
}

// ---------- 3. LEADERBOARD ----------
// Steve directive 2026-05-22: "top 10 by composite" duplicates the Alpha
// Zone basket. Show the biggest composite gainers over the leaderboard
// lookback window instead — distinct content (direction vs absolute rank).
function LeaderboardTile() {
  const { data } = useResource<LeaderboardResp>('/api/leaderboard', 60_000);
  const gainers = (data?.movers?.cGainers ?? []).slice(0, 10);
  return (
    <TileFrame
      num="3"
      title={`Leaderboard — Biggest Gainers (${data?.lookbackDays ?? 36}d Δ Composite)`}
      asOf={data?.lookbackAnchor ? `since ${data.lookbackAnchor}` : null}
    >
      <ul className="space-y-0.5">
        {gainers.map((s, i) => (
          <li key={s.symbol} className="grid grid-cols-[18px_60px_1fr_auto] gap-2 items-baseline text-[11px] border-b border-[#0f141c] pb-0.5">
            <span className="text-[#64748b] font-mono text-right tabular-nums">{i + 1}</span>
            <span className="text-white font-mono font-bold">{s.symbol}</span>
            <span className="text-[#94a3b8] truncate text-[10px]">{s.name}</span>
            <span className="text-emerald-300 font-mono font-bold tabular-nums">+{s.cDelta.toFixed(2)}</span>
          </li>
        ))}
      </ul>
    </TileFrame>
  );
}

// ---------- 4. HISTORY LAB ----------
// Mirrors /research-desk/history-lab — SPX-universe valuation expensiveness
// and quality strength percentiles vs ~27 years of own history. Two
// percentile rings, sized to fit inside the slide tile.
function HistoryLabTile() {
  const { data } = useResource<RegimeResp>('/api/history/universe-regime', 60_000);
  const val = data?.composites?.valuationExpensiveness;
  const qua = data?.composites?.qualityStrength;
  return (
    <TileFrame
      num="4"
      title="History Lab — SPX Universe"
      asOf={data ? `vs ~27-yr history · ${data.startDate?.slice(0, 4) ?? ''} → today` : null}
    >
      {!data ? (
        <div className="text-[11px] text-[#64748b] italic">Loading regime…</div>
      ) : (
        <div className="flex items-center justify-around h-full gap-2">
          <PercentileRing
            value={val?.value ?? null}
            label="Valuation"
            sub={val?.value != null ? (val.value >= 75 ? 'more expensive than usual' : val.value >= 40 ? 'about average' : 'cheaper than usual') : '—'}
            tone="red"
          />
          <PercentileRing
            value={qua?.value ?? null}
            label="Quality"
            sub={qua?.value != null ? (qua.value >= 75 ? 'better than usual' : qua.value >= 40 ? 'about average' : 'weaker than usual') : '—'}
            tone="emerald"
          />
        </div>
      )}
    </TileFrame>
  );
}

function PercentileRing({ value, label, sub, tone }: { value: number | null; label: string; sub: string; tone: 'red' | 'emerald' }) {
  const pct = value ?? 0;
  const stroke = tone === 'red' ? '#ef4444' : '#10b981';
  const txt = tone === 'red' ? 'text-red-300' : 'text-emerald-300';
  const R = 34, C = 2 * Math.PI * R;
  const dash = `${(pct / 100) * C} ${C}`;
  const display = value == null ? '—' : `${pct.toFixed(0)}${suffix(Math.round(pct))}`;
  return (
    <div className="flex items-center gap-3">
      <svg width="86" height="86" viewBox="0 0 86 86">
        <circle cx="43" cy="43" r={R} stroke="#1e293b" strokeWidth="6" fill="none" />
        <circle
          cx="43" cy="43" r={R}
          stroke={stroke} strokeWidth="6" fill="none"
          strokeDasharray={dash}
          strokeLinecap="round"
          transform="rotate(-90 43 43)"
        />
      </svg>
      <div>
        <div className="text-[9px] uppercase tracking-widest text-[#94a3b8] font-bold">{label}</div>
        <div className={`text-[26px] font-bold tabular-nums leading-none ${txt}`}>{display}</div>
        <div className="text-[10px] text-[#94a3b8] mt-1 leading-tight max-w-[120px]">{sub}</div>
      </div>
    </div>
  );
}

function suffix(n: number): string {
  const last2 = n % 100;
  if (last2 >= 11 && last2 <= 13) return 'th';
  switch (n % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
}
